import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import HQLayout from "./HQLayout";
import MorningBrief from "./MorningBrief";
import IdaRadar from "./IdaRadar";
import WeeklyDigestCard from "./WeeklyDigestCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  exploring: { label: "Exploring", cls: "text-sky-300 border-sky-300/30 bg-sky-300/10" },
  producer_review: { label: "For Review", cls: "text-amber-300 border-amber-300/30 bg-amber-300/10" },
  approved: { label: "Approved", cls: "text-emerald-300 border-emerald-300/30 bg-emerald-300/10" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "producer_review", label: "For Review" },
  { key: "exploring", label: "Exploring" },
  { key: "approved", label: "Approved" },
];

function scoreColor(n) {
  if (n >= 8) return "text-emerald-300";
  if (n >= 5) return "text-amber-300";
  return "text-white/40";
}

export default function HQEnquiries() {
  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({});
  const [stage, setStage] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hq/projects`, { params: stage ? { stage } : {} });
        setProjects(data.projects || []);
        setCounts(data.counts || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [stage]);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <HQLayout>
      <main className="px-6 md:px-12 py-10 md:py-14 max-w-[1080px]">
        <div className="text-[11px] uppercase tracking-[0.16em] text-white/40">Producer Dashboard</div>
        <h1 className="mt-3 font-display font-extrabold text-[34px] md:text-[44px] tracking-[-0.03em] leading-[1.02]">
          Enquiries
        </h1>
        <p className="mt-3 text-white/50 text-[14.5px]">
          {counts.producer_review > 0
            ? `${counts.producer_review} waiting for your review.`
            : "Nothing waiting on you right now."}
        </p>

        <MorningBrief />

        <IdaRadar />

        <WeeklyDigestCard />

        <div className="mt-8 flex flex-wrap gap-2" data-testid="hq-stage-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStage(f.key)}
              className={`px-4 py-1.5 rounded-full text-[13px] border transition-colors ${
                stage === f.key
                  ? "bg-white text-[#0c111c] border-white"
                  : "text-white/60 border-white/15 hover:border-white/40"
              }`}
              data-testid={`hq-filter-${f.key || "all"}`}
            >
              {f.label}
              <span className="ml-1.5 opacity-60">{f.key ? counts[f.key] || 0 : total}</span>
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-3" data-testid="hq-enquiries-list">
          {loading && <div className="text-white/40 text-sm">Loading…</div>}
          {!loading && projects.length === 0 && (
            <div className="text-white/40 text-sm py-10">No enquiries here yet.</div>
          )}
          {!loading &&
            projects.map((p) => {
              const read = p.ida_read || {};
              const sm = STAGE_META[p.stage || "exploring"] || STAGE_META.exploring;
              const title = p.recommendation?.project_title || p.brief || "Untitled";
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`/hq/projects/${p.id}`)}
                  className="w-full text-left rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition-all p-5 md:p-6 group"
                  data-testid={`hq-enquiry-${p.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`font-display font-extrabold text-[26px] md:text-[30px] w-[52px] shrink-0 ${scoreColor(read.opportunity_score || 0)}`}>
                      {read.opportunity_score ? read.opportunity_score : "–"}
                      <span className="text-[12px] text-white/25 font-normal">/10</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display font-bold text-[16.5px] md:text-[18px] tracking-[-0.01em] truncate">{title}</span>
                        <span className={`text-[10.5px] uppercase tracking-[0.08em] border rounded-full px-2 py-0.5 ${sm.cls}`}>
                          {sm.label}
                        </span>
                      </div>
                      <div className="mt-1.5 text-white/50 text-[13px] truncate">
                        {read.one_liner || p.brief}
                      </div>
                      <div className="mt-1.5 text-white/30 text-[12px]">
                        {(p.contact?.name || "Unknown client")} · {new Date(p.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                        {Number(p.version) > 1 ? ` · v${p.version}` : ""}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/70 transition-colors shrink-0" />
                  </div>
                </button>
              );
            })}
        </div>
      </main>
    </HQLayout>
  );
}
